import React, { useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import MapPicker from "./MapPicker";
import InputField from "components/fields/InputField";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import EventService from "services/events.service";
import Toast from "components/toaster";
import Select from "react-select";
import Loader from "../../assets/img/loader.gif";
import { IoLocationSharp } from "react-icons/io5";
import EventTypesService from "services/eventTypes.service";
import CroppedImage from "./CroppedImage";
import imageCompression from "browser-image-compression";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CreateEventPosts = ({ type, isOpen, onClose, getEvents, eventData }) => {
  const [categories, setCategories] = useState([]);
  const [carTypes, setCarTypes] = useState([]);
  const [category, setCategory] = useState(null);
  const [carType, setCarType] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [isLoading, setIsLoading] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [marker, setMarker] = useState(null);
  const [initialImage, setInitialImage] = useState("");
  const [croppedImage, setCroppedImage] = useState("");
  const [selectedScale, setSelectedScale] = useState("16:9"); // Default scale

  const validationSchema = Yup.object({
    typeid: Yup.number().oneOf([1, 2]).required(),
    type: Yup.string().oneOf(["event", "otherType"]).required(), // replace 'otherType' as needed
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    website: Yup.string()
      .nullable()
      .test('is-url', 'Please enter a valid URL', function(value) {
        if (!value || value.length === 0) return true; // Allow empty values
        return /^https?:\/\/.+/.test(value); // Validate URL format if provided
      }),
    images: Yup.array().of(Yup.mixed()),
    categoryId: Yup.string().required("Category is required"),
    carTypeId: Yup.string().required("Car type is required"),
    startdate: Yup.date()
      .required("Start date is required")
      .typeError("Invalid date format"),
    enddate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startdate"), "End date cannot be before start date")
      .typeError("Invalid date format"),
    likecount: Yup.number()
      .min(0, "Like count cannot be negative")
      .integer("Like count must be a whole number")
      .required("Like count is required"),

    location: Yup.object({
      type: Yup.string().required("Location type is required"),
      coordinates: Yup.array()
        .of(Yup.number())
        .length(2, "Coordinates must be [longitude, latitude]")
        .required(),
    }),
    address: Yup.string().required("Address is required"),
    zipcode: Yup.string().required("Zipcode is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().required("Country is required"),
  });

  const colourStyles = {
    option: (styles) => {
      return {
        ...styles,
        backgroundColor: "rgb(30 30 32 / 1) !important",
        ":hover": {
          backgroundColor: "rgba(255, 255, 255, 0.30) !important",
          color: "#ffffff",
        },
      };
    },
  };

  const formik = useFormik({
    initialValues: {
      typeid: type === "event" ? 2 : 1,
      type: type,
      title: "",
      description: "",
      website: "",
      images: [],
      members: [],
      categoryId: "",
      carTypeId: "",
      startdate: "",
      enddate: "",
      likecount: 0,

      location: {
        type: "Point",
        coordinates: [],
      },
      address: "",
      zipcode: "",
      city: "",
      state: "",
      country: "",
      is_organization_event: true,
      is_advertisement: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading("submit");
      console.log("Current selectedScale:", selectedScale);
      console.log("Original values:", values);
      
      // Create body object without website if it's empty
      const { website, ...valuesWithoutWebsite } = values;
      console.log("Website value:", website);
      console.log("Values without website:", valuesWithoutWebsite);
      
      const body = {
        ...valuesWithoutWebsite,
        startdate: moment.utc(values.startdate).toISOString(),
        enddate: moment.utc(values.enddate).toISOString(),
        scale: selectedScale,
      };
      
      // Only add website to body if it's not empty
      if (website && website.trim() !== "") {
        body.website = website;
        console.log("Added website to body:", website);
      } else {
        console.log("Website is empty, not adding to body");
      }
      
      console.log("🚀 ~ onSubmit: ~ body:", body);
      try {
        if (body.post_event_id) {
          const response = await EventService.updateEvent(body);
          Toast("success", response?.data?.message);
        } else {
          const response = await EventService.createEvent(body);
          Toast("success", response?.data?.message);
        }
        onClose();
        getEvents();
      } catch (error) {
        Toast("error", error.response.data.message);
      } finally {
        setIsLoading("");
      }
    },
  });

  useEffect(() => {
    if (eventData) {
      formik.setFieldValue("post_event_id", eventData?._id);
      formik.setFieldValue("title", eventData?.title);
      formik.setFieldValue("description", eventData?.description);
      formik.setFieldValue("website", eventData?.website);
      formik.setFieldValue("images", eventData?.images);
      setSelectedImage(eventData?.images?.[0]);
      formik.setFieldValue("categoryId", eventData?.categoryId);
      setCategory(eventData?.categoryId);
      formik.setFieldValue("carTypeId", eventData?.carTypeId);
      setCarType(eventData?.carTypeId);
      formik.setFieldValue("startdate", eventData?.startdate);
      formik.setFieldValue("enddate", eventData?.enddate);
      formik.setFieldValue("likecount", eventData?.likecount !== undefined ? eventData?.likecount : 0);
      formik.setFieldValue("location.type", eventData?.location?.type);
      formik.setFieldValue(
        "location.coordinates",
        eventData?.location?.coordinates
      );
      setMarker({
        lat: eventData?.location?.coordinates[1],
        lng: eventData?.location?.coordinates[0],
      });
      formik.setFieldValue("address", eventData?.address);
      formik.setFieldValue("zipcode", eventData?.zipcode);
      formik.setFieldValue("city", eventData?.city);
      formik.setFieldValue("state", eventData?.state);
      formik.setFieldValue("country", eventData?.country);
      formik.setFieldValue(
        "is_organization_event",
        eventData?.is_organization_event
      );
      formik.setFieldValue("is_advertisement", eventData?.is_advertisement);
      setSelectedScale(eventData?.scale || "1:1"); // Load scale from event data or default to "1:1"
    }
  }, [isOpen]);

  const getAllCategory = async () => {
    const body = {
      pagination: {
        projection: {},
        filters: {},
        sort: {},
      },
      searchtext: "",
    };

    try {
      const response = await EventService.getAllCategory(body);
      const data = response?.data?.data?.map((item) => ({
        label: item?.type,
        value: item?._id,
      }));
      setCategories(data);
    } catch (error) {
      Toast("error", error.response.data.message);
    }
  };

  const getCarTypes = async () => {
    try {
      const body = {
        pagination: {
          projection: {},
          filters: {},
          sort: {},
        },
        searchF43text: "",
      };
      const response = await EventTypesService.getAllEventTypes(body);
      const data = response?.data?.data?.map((item) => ({
        label: item?.type,
        value: item?._id,
      }));
      setCarTypes(data);
    } catch (error) {
      Toast("error", error.response.data.message);
    }
  };

  const handleMapClose = () => {
    setShowMap(false);
  };

  const handleMapOpen = () => {
    setShowMap(true);
  };

  useEffect(() => {
    getAllCategory();
    getCarTypes();
  }, []);

  const handleSelectCategory = async (option) => {
    setCategory(option.value);
    formik.setFieldValue("categoryId", option.value);
  };

  const handleSelectCarType = async (option) => {
    setCarType(option.value);
    formik.setFieldValue("carTypeId", option.value);
  };

  const handleAspectRatioSelect = (scale) => {
    console.log("Selected scale:", scale);
    setSelectedScale(scale);
    console.log("Updated selectedScale state:", scale);
  };

  const handleLikeCountChange = (e) => {
    const value = e.target.value;
    // Only allow non-negative integers
    const numericValue = value.replace(/[^0-9]/g, '');
    
    // If the value is empty, set it to empty string
    if (numericValue === '') {
      formik.setFieldValue("likecount", "");
    } else {
      // Convert to number and ensure it's non-negative
      const numValue = parseInt(numericValue, 10);
      if (numValue >= 0) {
        formik.setFieldValue("likecount", numValue);
      } else {
        formik.setFieldValue("likecount", "");
      }
    }
  };

  const handleLikeCountKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow numbers
        (e.keyCode >= 48 && e.keyCode <= 57) ||
        // Allow numpad numbers
        (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Prevent any other key
    e.preventDefault();
  };

  const handleImageChnage = async (e) => {
    const file = e.target.files[0];
    setInitialImage(file);

    e.target.value = "";
  };

  const uploadImage = async () => {
    setIsLoading("image");
    try {
      const options = {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 1000,
        useWebWorker: true,
        initialQuality: 0.8,
      };

      const compressedFile = await imageCompression(croppedImage, options);

      const formData = new FormData();
      formData.append("files", compressedFile);
      const response = await EventService.fileUpload(formData);

      setSelectedImage(response?.data?.files?.[0]);
      formik.setFieldValue("images", [response?.data?.files?.[0]]);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setIsLoading("");
    }
  };

  useEffect(() => {
    if (croppedImage) {
      uploadImage();
    }
  }, [croppedImage]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-2 sm:p-4">
      <div className="flex h-[90vh] max-h-[90vh] w-[95%] sm:w-[700px] flex-col rounded-xl bg-navy-800 shadow-lg overflow-hidden">
        {/* Fixed Header */}
        <div className="relative flex justify-center border-b border-white/10 py-2 sm:py-4 px-3 sm:px-6">
          <p className="text-lg sm:text-2xl font-semibold text-white">Add Event</p>
          <div className="absolute left-3 sm:left-6 text-xs sm:text-sm text-gray-300">
            Scale: {selectedScale}
          </div>
          <IoMdClose
            className="absolute right-3 sm:right-6 cursor-pointer text-xl sm:text-2xl text-white hover:text-gray-300"
            onClick={onClose}
          />
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-3 sm:py-4">
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <div className="grid h-full grid-cols-1   gap-3 md:grid-cols-2">
              <div>
                <InputField
                  variant="form"
                  
                  extra="mb-2"
                  label="Title"
                  placeholder="Enter your title"
                  id="title"
                  type="text"
                  name="title"
                  onChange={formik.handleChange}
                  value={formik.values.title}
                />
                {formik.touched.title && formik.errors.title && (
                  <p className="validation-error">{formik.errors.title}</p>
                )}
              </div>

              <div>
                <InputField
                  variant="form"
                  extra="mb-2"
                  label="Description"
                  placeholder="Enter your description"
                  id="description"
                  type="text"
                  name="description"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                />
                {formik.touched.description && formik.errors.description && (
                  <p className="validation-error">{formik.errors.description}</p>
                )}
              </div>

              <div>
                <InputField
                  variant="form"
                  extra="mb-2"
                  label="Web URL (Optional)"
                  placeholder="Enter your web URL"
                  id="website"
                  type="text"
                  name="website"
                  onChange={formik.handleChange}
                  value={formik.values.website}
                />
                {formik.touched.website && formik.errors.website && (
                  <p className="validation-error">{formik.errors.website}</p>
                )}
              </div>

              <div>
                <InputField
                  variant="form"
                  extra="mb-2"
                  label="Like Count"
                  placeholder="Enter like count"
                  id="likecount"
                  type="number"
                  name="likecount"
                  onChange={handleLikeCountChange}
                  onKeyDown={handleLikeCountKeyDown}
                  value={formik.values.likecount}
                />
                {formik.touched.likecount && formik.errors.likecount && (
                  <p className="validation-error">{formik.errors.likecount}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="startdate"
                  className={`mb-2 ml-3 text-sm font-bold text-white`}
                >
                  Start Date & Time
                </label>
                <DatePicker
                  selected={
                    formik.values.startdate
                      ? moment(formik.values.startdate).toDate()
                      : null
                  }
                  onChange={(date) => {
                    const localDate = moment(date).toISOString(); // convert local to UTC ISO string
                    formik.setFieldValue("startdate", localDate);
                  }}
                  showTimeSelect
                  timeFormat="hh:mm a"
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="yyyy-MM-dd hh:mm a"
                  className="mt-2 flex h-12 !w-full items-center justify-center rounded-xl border !border-white/10 border-gray-200 bg-white/0 p-3 text-sm text-white outline-none placeholder:!text-[#8E8E8E]"
                  minDate={new Date()}
                  placeholderText="Select Start date & time"
                  minTime={
                    moment().isSame(
                      moment(formik.values.startdate).startOf("day"),
                      "day"
                    )
                      ? moment().toDate()
                      : moment().startOf("day").toDate()
                  }
                  maxTime={moment().endOf("day").toDate()}
                  onKeyDown={(e) => e.preventDefault()}
                />

                {formik.touched.startdate && formik.errors.startdate && (
                  <p className="validation-error">{formik.errors.startdate}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="enddate"
                  className={`mb-2 ml-3 text-sm font-bold text-white`}
                >
                  End Date & Time
                </label>
                <DatePicker
                  selected={
                    formik.values.enddate
                      ? moment(formik.values.enddate).toDate()
                      : null
                  }
                  onChange={(date) => {
                    const localDate = moment(date).toISOString();
                    formik.setFieldValue("enddate", localDate);
                  }}
                  showTimeSelect
                  timeFormat="hh:mm a"
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="yyyy-MM-dd hh:mm a"
                  className="mt-2 flex h-12 !w-full items-center justify-center rounded-xl border !border-white/10 border-gray-200 bg-white/0 p-3 text-sm text-white outline-none placeholder:!text-[#8E8E8E]"
                  minDate={
                    formik.values.startdate
                      ? moment(formik.values.startdate).toDate()
                      : new Date()
                  }
                  placeholderText="Select End date & time"
                  minTime={
                    formik.values.startdate &&
                    formik.values.enddate &&
                    moment(formik.values.enddate).isSame(
                      moment(formik.values.startdate),
                      "day"
                    )
                      ? moment(formik.values.startdate).toDate()
                      : moment().startOf("day").toDate()
                  }
                  maxTime={moment().endOf("day").toDate()}
                  onKeyDown={(e) => e.preventDefault()}
                />

                {formik.touched.enddate && formik.errors.enddate && (
                  <p className="validation-error">{formik.errors.enddate}</p>
                )}
              </div>

              <div className="mb-2 flex w-full flex-col justify-between">
                <label
                  htmlFor="categoryId"
                  className={`ml-3 text-sm font-bold text-white`}
                >
                  Category
                </label>
                <Select
                  className="!relative mb-3 mt-2 w-full !text-white"
                  classNamePrefix="select"
                  defaultValue={categories[0]}
                  name="Category"
                  options={categories}
                  value={categories.find((option) => option?.value == category)}
                  onChange={handleSelectCategory}
                  styles={colourStyles}
                />
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <p className="validation-error">{formik.errors.categoryId}</p>
                )}
              </div>

              <div className="mb-2 flex w-full flex-col justify-between">
                <label
                  htmlFor="categoryId"
                  className={`ml-3 text-sm font-bold text-white`}
                >
                  Car Type
                </label>
                <Select
                  className="!relative mb-3 mt-2 w-full !text-white"
                  classNamePrefix="select"
                  defaultValue={carTypes[0]}
                  name="Category"
                  options={carTypes}
                  value={carTypes.find((option) => option?.value == carType)}
                  onChange={handleSelectCarType}
                  styles={colourStyles}
                />
                {formik.touched.carTypeId && formik.errors.carTypeId && (
                  <p className="validation-error">{formik.errors.carTypeId}</p>
                )}
              </div>

              <div
                className="flex h-full min-h-11 w-full cursor-pointer items-center justify-center gap-1 rounded-xl border border-white/10 duration-300 hover:bg-white/10"
                onClick={handleMapOpen}
              >
                <IoLocationSharp className="text-red-500" size={18} />
                Choose Your Location
              </div>

              <div className="h-full">
                <input
                  type="file"
                  hidden
                  id="upload"
                  accept="image/*"
                  onChange={handleImageChnage}
                />
                {isLoading === "image" ? (
                  <div className="flex min-h-11 w-full items-center justify-center rounded-xl border border-dashed border-white/10">
                    <img
                      src={Loader}
                      alt="Loading..."
                      className="mx-auto h-10 w-10"
                    />
                  </div>
                ) : (
                  <div className="">
                    {selectedImage ? (
                      <label htmlFor="upload" className="mb-5 mt-1 h-full w-full">
                        <img
                          src={selectedImage}
                          className="h-full w-full cursor-pointer rounded-xl object-cover"
                        />
                      </label>
                    ) : (
                      <label
                        htmlFor="upload"
                        className="flex h-full min-h-11 w-full cursor-pointer items-center justify-center rounded-xl border border-dashed border-white/10 duration-300 hover:bg-white/10"
                      >
                        Choose File
                      </label>
                    )}
                  </div>
                )}

                {formik.touched.images && formik.errors.images && (
                  <p className="validation-error">{formik.errors.images}</p>
                )}
              </div>

              {formik.values.address && (
                <>
                  <div>
                    <InputField
                      variant="form"
                      extra="mb-2"
                      label="Address"
                      placeholder="Enter your Address"
                      id="address"
                      type="text"
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.address && formik.errors.address && (
                      <p className="validation-error">{formik.errors.address}</p>
                    )}
                  </div>

                  <div>
                    <InputField
                      variant="form"
                      extra="mb-2"
                      label="City"
                      placeholder="Enter your city"
                      id="city"
                      type="text"
                      name="city"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.city && formik.errors.city && (
                      <p className="validation-error">{formik.errors.city}</p>
                    )}
                  </div>
                  <div>
                    <InputField
                      variant="form"
                      extra="mb-2"
                      label="State"
                      placeholder="Enter your state"
                      id="state"
                      type="text"
                      name="state"
                      value={formik.values.state}
                    />
                    {formik.touched.state && formik.errors.state && (
                      <p className="validation-error">{formik.errors.state}</p>
                    )}
                  </div>
                  <div>
                    <InputField
                      variant="form"
                      extra="mb-2"
                      label="Country"
                      placeholder="Enter your country"
                      id="country"
                      type="text"
                      name="country"
                      value={formik.values.country}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.country && formik.errors.country && (
                      <p className="validation-error">{formik.errors.country}</p>
                    )}
                  </div>
                  <div>
                    <InputField
                      variant="form"
                      extra="mb-2"
                      label="Zipcode"
                      placeholder="Enter your zipcode"
                      id="zipcode"
                      type="text"
                      name="zipcode"
                      value={formik.values.zipcode}
                      onChange={(e) => {
                        const onlyNumbers = e.target.value.replace(/\D/g, "");
                        formik.setFieldValue("zipcode", onlyNumbers);
                      }}
                    />
                    {formik.touched.zipcode && formik.errors.zipcode && (
                      <p className="validation-error">{formik.errors.zipcode}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {initialImage && (
              <div className="mt-2">
                <CroppedImage
                  handleMapClose={() => setInitialImage("")}
                  initialImage={initialImage}
                  setInitialImage={setInitialImage}
                  setSelectedImage={setSelectedImage}
                  setCroppedImage={setCroppedImage}
                  onAspectRatioSelect={handleAspectRatioSelect}
                  selectedScale={selectedScale}
                />
              </div>
            )}

            {showMap && (
              <div className="mt-2">
                <MapPicker
                  formik={formik}
                  setShowMap={setShowMap}
                  marker={marker}
                  setMarker={setMarker}
                  handleMapClose={handleMapClose}
                />
              </div>
            )}
          </form>
        </div>

        {/* Fixed Footer */}
        {formik.values.location.coordinates.length > 0 && (
          <div className="border-t border-white/10 px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex justify-end">
              {isLoading === "submit" ? (
                <div className="flex h-10 w-[81.86px] items-center rounded-full bg-orangePrimary px-4 text-white">
                  <img
                    src={Loader}
                    alt="Loading..."
                    className="mx-auto h-10 w-10"
                  />
                </div>
              ) : (
                <button
                  type="submit"
                  onClick={formik.handleSubmit}
                  className="flex h-10 w-fit cursor-pointer items-center rounded-full bg-orangePrimary px-4 text-white duration-150 hover:bg-orangePrimary/80"
                >
                  Submit
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateEventPosts;
